import React, { useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Sprout, Sparkles, Flame } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function Habits() {
  const { t, lang } = useLang();
  const [date, setDate] = useState(todayISO());
  const [water, setWater] = useState(0);
  const [steps, setSteps] = useState(0);
  const [sleep, setSleep] = useState(0);
  const [mood, setMood] = useState(3);
  const [ateHealthy, setAteHealthy] = useState(false);
  const [summary, setSummary] = useState({ logs: [], streak: 0 });
  const [insight, setInsight] = useState("");
  const [loadingInsight, setLoadingInsight] = useState(false);

  const load = async () => {
    const { data } = await api.get("/habits/summary");
    setSummary(data);
    const todaysLog = (data.logs || []).find((l) => l.date === date);
    if (todaysLog) {
      setWater(todaysLog.water_glasses || 0);
      setSteps(todaysLog.steps || 0);
      setSleep(todaysLog.sleep_hours || 0);
      setMood(todaysLog.mood || 3);
      setAteHealthy(!!todaysLog.ate_healthy);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.post("/habits/log", {
      date,
      water_glasses: Number(water),
      steps: Number(steps),
      sleep_hours: Number(sleep),
      mood: Number(mood),
      ate_healthy: ateHealthy,
    });
    toast.success(lang === "id" ? "Tersimpan" : "Saved");
    await load();
  };

  const fetchInsight = async () => {
    setLoadingInsight(true);
    try {
      const { data } = await api.get("/habits/insight", { params: { lang } });
      setInsight(data.insight || "");
    } finally {
      setLoadingInsight(false);
    }
  };

  const chartData = (summary.logs || [])
    .slice(0, 14)
    .reverse()
    .map((l) => ({ date: l.date.slice(5), steps: l.steps || 0, water: l.water_glasses || 0, sleep: l.sleep_hours || 0 }));

  return (
    <div className="space-y-8" data-testid="habits-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A5D23] font-bold">Gracely</p>
        <h1 className="font-serif-display text-4xl text-stone-900 mt-2">{t("habits_title")}</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="card-soft p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center"><Flame className="w-5 h-5" /></div>
            <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{t("dash_streak")}</p>
          </div>
          <p className="mt-6 font-serif-display text-5xl text-stone-900">{summary.streak || 0}<span className="text-lg text-stone-400 ml-2">{lang === "id" ? "hari" : "days"}</span></p>
        </div>
        <div className="card-soft p-6 md:col-span-2">
          <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Konsistensi 14 hari" : "14-day consistency"}</p>
          <div className="mt-4 h-40">
            {chartData.length === 0 ? (
              <p className="text-stone-500 text-sm">{lang === "id" ? "Belum ada data." : "No data yet."}</p>
            ) : (
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#f0efec" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} stroke="#a8a29e" />
                  <YAxis fontSize={11} stroke="#a8a29e" />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 12 }} />
                  <Bar dataKey="steps" fill="#4A5D23" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="font-serif-display text-2xl text-stone-900">{lang === "id" ? "Log hari ini" : "Today's log"}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-stone-700">{t("water")}</Label>
            <Input type="number" value={water} onChange={(e) => setWater(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent" data-testid="habit-water" />
          </div>
          <div>
            <Label className="text-stone-700">{t("steps")}</Label>
            <Input type="number" value={steps} onChange={(e) => setSteps(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent" data-testid="habit-steps" />
          </div>
          <div>
            <Label className="text-stone-700">{t("sleep")}</Label>
            <Input type="number" step="0.5" value={sleep} onChange={(e) => setSleep(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent" data-testid="habit-sleep" />
          </div>
          <div>
            <Label className="text-stone-700">{t("mood")}</Label>
            <Input type="number" min="1" max="5" value={mood} onChange={(e) => setMood(e.target.value)} className="mt-1 rounded-xl bg-stone-50 border-transparent" data-testid="habit-mood" />
          </div>
          <label className="flex items-center gap-3 mt-6 md:mt-0">
            <Checkbox checked={ateHealthy} onCheckedChange={(v) => setAteHealthy(!!v)} data-testid="habit-ate-healthy" />
            <span className="text-stone-700 text-sm">{t("ate_healthy")}</span>
          </label>
        </div>
        <Button onClick={save} className="mt-6 rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white btn-pop" data-testid="save-log-btn">
          <Sprout className="w-4 h-4 mr-1" /> {t("save_log")}
        </Button>
      </div>

      <div className="card-soft p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-serif-display text-2xl text-stone-900">{lang === "id" ? "Wawasan dari Gracely" : "Gracely's insights"}</h2>
          <Button onClick={fetchInsight} disabled={loadingInsight} className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white btn-pop" data-testid="get-insight-btn">
            <Sparkles className="w-4 h-4 mr-1" /> {loadingInsight ? "…" : t("get_insight")}
          </Button>
        </div>
        {insight ? (
          <div className="mt-4 p-4 rounded-xl bg-[#EAF0E4] whitespace-pre-wrap text-stone-800 leading-relaxed text-sm" data-testid="insight-text">{insight}</div>
        ) : (
          <p className="mt-3 text-sm text-stone-500">{lang === "id" ? "Klik tombol untuk mendapatkan prediksi & rekomendasi." : "Click the button to get predictions & recommendations."}</p>
        )}
      </div>
    </div>
  );
}
